import React, { useCallback, useContext, useMemo } from 'react';
import ClockIcon from '@atlaskit/icon/core/clock';
import {
	FlyoutMenuItem,
	FlyoutMenuItemTrigger,
	FlyoutMenuItemContent,
} from '@atlaskit/navigation-system';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { NAVIGATION_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { SECTION_ITEM_RECENT } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { NavItemsContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { L1_MENU_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useFormattedMessageAndIcon } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME, RECENT_QUERY_LIMIT } from '../common/constants.tsx';
import messages from './messages.tsx';
import { sidebarRecentMenuEntryPoint } from './recent-menu/entrypoint.tsx';

const EMPTY_OBJECT = {};

export function RecentMenu(): React.JSX.Element {
	const cloudId = useCloudId();
	const { formattedMessage, icon } = useFormattedMessageAndIcon(messages.recent, ClockIcon);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		sidebarRecentMenuEntryPoint,
		useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);
	const { moreMenuIds } = useContext(NavItemsContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onOpenChange = useCallback(
		(isMenuOpen: boolean) => {
			if (isMenuOpen) {
				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.RECENT);
				fireUIAnalytics(event, SECTION_ITEM_RECENT, { isInMore });
			}
		},
		[moreMenuIds, createAnalyticsEvent],
	);
	return (
		<FlyoutMenuItem onOpenChange={onOpenChange}>
			<FlyoutMenuItemTrigger
				interactionName="nav4-sidebar-recent"
				ref={triggerRef}
				elemBefore={icon}
			>
				{formattedMessage}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent>
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="nav4-sidebar-recent-default"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					errorFallback="flag"
					fallback={
						<SkeletonNavMenuItems withHeading withSearch withSubHeading withSubTitle isFlyout />
					}
					runtimeProps={EMPTY_OBJECT}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
